import { memo, type ReactNode, useCallback, useLayoutEffect, useState } from "react";
import { ReduxSortingPicker } from "../ReduxSortingPicker";
import { SearchDisplayMode } from "../../../models";
import SearchResultList from "../ListView/SearchResultList";
import Pagination from "../Pagination";
import { NoSearchResults } from "../NoSearchResults";
import { useDispatch, useSelector } from "react-redux";
import {
  selectBeSortCodeNested,
  selectIsInFlight,
  selectIsInit,
  selectProductPageAmount,
  selectProductResults,
  selectSearchDisplayMode,
} from "../../../redux/searchSelectors";
import { type RootState } from "../../../../redux/store";
import {
  changePage,
  changeSearchDisplayModeAction,
  clearAll,
  clearAllTechFilters,
  handleMissingSortAfterTabChangeThunk,
  updateSearchText,
} from "../../../redux/actions";
import NotFoundContactUsBar from "../../NotFoundContactUsBar/NotFoundContactUsBar";
import DisclaimerCountryOrigin from "../../../../common/components/Messages/DisclaimerCountryOrigin";
import { ReduxProductTabularView } from "../TabularView/ReduxProductTabularView";
import { useIsMinDesktopSize } from "../../../../common/hooks/useMediaQuery";
import { useResetQualtrics } from "../../../../common/hooks/useResetQualtrics";
import { type SearchPageState } from "../../../redux/types";
import {
  ImperialToggle,
  useReduxUnitMode,
} from "../../../../ImperialMetricData/components/ImperialToggle/ImperialToggle";
import { SeriesTreeView } from "../SeriesTreeView/SeriesTreeView";
import { type UnitMode } from "../../../../ImperialMetricData/redux/UnitMode";
import { SearchPageType } from "../../../useSearchPageType";
import { trackTreesInteraction } from "../../../../feature/Analytics/ga/ga";
import { IncludesSurchargeDisclaimer } from "~/common/components/Messages/IncludesSurchargeDisclaimer";
import { type PdpPageProductData } from "~/ProductDetails/models/PdpPageProductData";
import { selectProductDescriptor } from "~/redux/products/productsSelectors";
import { useTranslation } from "react-i18next";
import { DisplayModeButtonList } from "~/Search/components/SearchResults/SearchResults/SearchResultsDisplayMode";
import { useSearchPageType } from "~/Search/useSearchPageType";
import { getInitialPriceReference } from "~/AddToCart/redux/addToCart/getInitialPriceReference";
import { getInitialResultsViewMode } from "~/Search/utils/searchUtils";
import classes from "./ProductResults.module.less";
import { Alert } from "~/common/m3/Alert/Alert";
import { ViewChip } from "../../mobileChips/ViewChip";
import { Divider, InputLabel, Stack, styled } from "@mui/material";
import { FilterSortChip } from "~/Search/components/mobileChips/FilterSortChip";
import { TechCategoryChip } from "~/Search/components/mobileChips/TechCategoryChip";
import SearchInput from "~/common/components/SearchInput";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import { useSelectAppliedFilters } from "~/Search/components/Filters/components/AppliedFilters/useSelectAppliedFilters";

interface Props {
  recommendationPosition?: number;
  recommendationComponent?: ReactNode;
}

const ProductResults = ({ recommendationPosition, recommendationComponent }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentPage = useSelector(selectCurrentPage);
  const isProductFamilySelected = useSelector(selectIsProductFamilySelected);
  const pageAmount = useSelector(selectProductPageAmount);
  const results = useSelector(selectProductResults);
  const appliedFilters = useSelectAppliedFilters();

  const pageType = useSearchPageType();
  // TODO - other cases! Campaign page?
  const gaInteractionType = pageType === SearchPageType.SEARCH ? "Search" : "PLP";
  const showTreeButton = pageType !== SearchPageType.Campaign;
  const showQueryInput = pageType === SearchPageType.Campaign;

  const inFlight = useSelector(selectIsInFlight);
  const isInit = useSelector(selectIsInit);

  const isDesktop = useIsMinDesktopSize();

  const displayMode = useSelector(selectSearchDisplayMode);
  const setDisplayMode = useCallback((mode: SearchDisplayMode) => dispatch(changeSearchDisplayModeAction(mode)), []);

  const productCodes = useSelector((state: RootState) => state.srp.results.map((row) => row.productCode));

  const productDescriptorList: ProductDescriptor<PdpPageProductData>[] = useSelector((state: RootState) => {
    return productCodes.map(
      (productCode) =>
        selectProductDescriptor(state, {
          productCode,
          priceReference: getInitialPriceReference(),
        }) as ProductDescriptor<PdpPageProductData>,
    );
  });

  const hasSurcharge = productDescriptorList.some(
    (product) => product.prices.length === 1 && (product.prices[0].surchargeScale[0]?.surcharge ?? 0) > 0,
  );

  const tariffSurcharge = productDescriptorList.some(
    (product) => product.prices.length === 1 && product.prices[0].isTariffSurchargeApplicable,
  );

  useLayoutEffect(() => {
    if (isInit && !isProductFamilySelected) {
      setDisplayMode(getInitialResultsViewMode());
    }
  }, [isInit, isProductFamilySelected]);

  const onPageChanged = (newPageNumber: number) => dispatch(changePage(newPageNumber));

  useResetQualtrics([inFlight]);

  let showPagination = pageAmount > 1;

  if (pageType === SearchPageType.SEARCH && displayMode === SearchDisplayMode.TABULAR && !isProductFamilySelected) {
    showPagination = false;
  } else if (displayMode === SearchDisplayMode.SERIES_TREE) {
    showPagination = false;
  }

  const srpState = useSelector((state: RootState) => state.srp);
  const funcDisplayMode = (targetDisplayMode: SearchDisplayMode) => {
    let display: "List" | "Tile" | "Tree";
    if (targetDisplayMode === SearchDisplayMode.LIST) {
      display = "Tile";
      dispatch(handleMissingSortAfterTabChangeThunk());
    } else if (targetDisplayMode === SearchDisplayMode.TABULAR) {
      display = "List";
    } else {
      display = "Tree";
    }
    trackTreesInteraction(gaInteractionType, display);
    setDisplayMode(targetDisplayMode);
  };

  const [searchQuery, setSearchQuery] = useState(srpState.text);

  const submit = () => {
    dispatch(updateSearchText(searchQuery));
  };

  const { value: unitMode, onChange } = useReduxUnitMode(gaInteractionType, () => dispatch(clearAllTechFilters()));

  const handleClearAll = useCallback(() => {
    dispatch(clearAll(pageType));
  }, [dispatch, pageType]);

  if (!results.length) {
    return (
      <div id={"productResults"}>
        <NoSearchResults variant={appliedFilters.length === 0 ? "search" : "filter"} onClearAll={handleClearAll} />
        <button hidden type="submit" />
      </div>
    );
  }

  const tabularViewConfigured = isProductFamilySelected || pageType === SearchPageType.PLP;

  return (
    <Stack
      id={"productResults"}
      sx={{
        gap: { phone: 2, "desktop-s": 3 },
      }}
    >
      {!isDesktop && (
        <Stack
          direction="row"
          sx={{
            gap: 1,
            overflow: "auto",
            scrollbarWidth: "none",
          }}
        >
          <ViewChip
            initialUnit={unitMode}
            initialViewMode={displayMode}
            applyValues={(unit, mode) => {
              onChange(unit);
              setDisplayMode(mode);
            }}
          />
          <Divider flexItem orientation="vertical" />

          <FilterSortChip />

          <TechCategoryChip index={0} />
          <TechCategoryChip index={1} />
        </Stack>
      )}

      {isDesktop && (
        <ActionBar>
          <DisplayModeButtonList
            displayMode={displayMode}
            setDisplayMode={funcDisplayMode}
            showSeriesTree={showTreeButton}
          />

          <ImperialToggle className={"scha__srp__action-bar__unit-toggle"} value={unitMode} onChange={onChange} />

          {displayMode === SearchDisplayMode.LIST && (
            <div>
              <InputLabel shrink>{t("searchPage.sort.title")}</InputLabel>
              <ReduxSortingPicker />
            </div>
          )}
        </ActionBar>
      )}
      {showQueryInput && (
        <SearchInput
          onSearch={submit}
          label="Search term"
          onChange={setSearchQuery}
          value={searchQuery}
          sx={{ width: "354px" }}
          size="small"
        />
      )}
      {displayMode === SearchDisplayMode.TABULAR && (
        <div className={classes.tabularContainer}>
          {recommendationComponent}
          {tabularViewConfigured && <ReduxProductTabularView key={getKey(srpState, unitMode)} />}
          {!tabularViewConfigured && (
            <Alert severity={"info"} className={"scha__srp__select-category-msg"}>
              {t("page.srp.selectCategory")}
            </Alert>
          )}
        </div>
      )}
      {displayMode === SearchDisplayMode.LIST && (
        <SearchResultList
          results={results}
          recommendationPosition={recommendationPosition}
          recommendationComponent={recommendationComponent}
        />
      )}
      {displayMode === SearchDisplayMode.SERIES_TREE && <SeriesTreeView />}
      {showPagination && (
        <Pagination
          classes={{ root: "scha__srp__pagination" }}
          pageAmount={pageAmount}
          currentPage={currentPage}
          onPageChange={onPageChanged}
          renderHrefs={true}
          goToIdAfterChange={"#resultsHeader"}
        />
      )}
      <NotFoundContactUsBar className="scha__srp__not-found-disc mb-2" />
      <div className="scha__srp__disclaimers">
        <Alert severity={"info"} className={"scha__srp__action-bar__imperial-msg"}>
          {t("page.srp.unitTypeMessage")}
        </Alert>
        <DisclaimerCountryOrigin />
      </div>
      {
        <IncludesSurchargeDisclaimer
          hasSurcharge={hasSurcharge}
          tariffSurcharge={tariffSurcharge}
          className={"mt-30"}
        />
      }
      <button hidden type="submit" />
    </Stack>
  );
};

const selectCurrentPage = (state: RootState) => state.srp.currentPage;
const selectIsProductFamilySelected = (state: RootState) => state.srp.selectedProductGroups.length > 0;

export default memo(ProductResults);

function getKey(state: SearchPageState, unitMode: UnitMode): string {
  const searchParams = new URLSearchParams();

  const selectedSort = selectBeSortCodeNested(state);
  if (selectedSort) {
    searchParams.set("sort", selectedSort);
  }

  const qParts = [];

  state.selectedProductGroups.forEach((group) => qParts.push(`categoryPath:${group}`));
  if (state.isSelectionAssistant && state.selectedProductGroups.length === 0) {
    qParts.push("categoryPath:/ROTARY");
  }

  if (qParts.length > 0) {
    searchParams.set("q", `${state.text}:${selectedSort}:${qParts.join(":")}`);
  } else {
    searchParams.set("q", `${state.text}:${selectedSort}`);
  }

  if (state.isSelectionAssistant) {
    searchParams.set("isSelectionAssistant", "true");
  }

  if (state.assistantValues.length > 0) {
    searchParams.set("assistantValues", state.assistantValues.join(","));
  }

  searchParams.set("unitMode", unitMode);

  return searchParams.toString();
}

const ActionBar = styled("div")({
  display: "grid",
  gridTemplateColumns: "minmax(max-content, 1fr) minmax(max-content, 1fr) 1fr",
  gap: "24px",
  whiteSpace: "nowrap",
});
